import { logAuditEntry } from './auditLog';

/**
 * #163 follow-up: the one way an admin records a manual (cash, pay on the
 * day) payment for a competition-day registration. Used by Registration
 * Details' "Mark payment received" and by the Competition Day Dashboard's
 * "Mark paid" and "Paid cash and approve". Same fields and the same
 * PAYMENT_RECEIVED audit entry as before; no squarePaymentId, so the
 * dashboard shows the Cash badge.
 */
export interface ManualPaymentRegistration {
  id: string;
  participantName?: string | null;
  participantEmail?: string | null;
  feeAmount?: number | null;
}

export async function recordManualPayment(
  client: any,
  registration: ManualPaymentRegistration,
  actor: { email: string; userId: string },
  /** Optional extra audit metadata (for example the household batch). */
  extraMetadata?: Record<string, unknown>,
): Promise<any> {
  const result = await client.models.CompetitionDayRegistration.update({
    id: registration.id,
    paymentStatus: 'RECEIVED',
    paymentTimestamp: new Date().toISOString(),
  });
  if (result?.errors?.length) {
    throw new Error(result.errors.map((e: any) => e?.message).join('; '));
  }
  logAuditEntry(actor.email, actor.userId, {
    actionType: 'PAYMENT_RECEIVED',
    targetEntityType: 'CompetitionDayRegistration',
    targetEntityId: registration.id,
    description: `Comp-day payment marked as received manually for ${registration.participantName} (${registration.participantEmail}) — $${registration.feeAmount}`,
    metadata: { amount: registration.feeAmount, ...(extraMetadata || {}) },
  });
  return result?.data;
}

/**
 * #163 follow-up (user, 17 Oct 2026): undo a desk payment ticked by mistake.
 * Only for a manual payment (no squarePaymentId) on a check-in that is not
 * yet approved; the dashboard enforces both before calling. Writes a
 * PAYMENT_REVERSED audit entry, the same action type Member Applications uses.
 */
export async function reverseManualPayment(
  client: any,
  registration: ManualPaymentRegistration,
  actor: { email: string; userId: string },
): Promise<any> {
  const result = await client.models.CompetitionDayRegistration.update({
    id: registration.id,
    paymentStatus: 'PENDING',
    paymentTimestamp: null,
  });
  if (result?.errors?.length) {
    throw new Error(result.errors.map((e: any) => e?.message).join('; '));
  }
  logAuditEntry(actor.email, actor.userId, {
    actionType: 'PAYMENT_REVERSED',
    targetEntityType: 'CompetitionDayRegistration',
    targetEntityId: registration.id,
    description: `Comp-day payment reversed (unticked) for ${registration.participantName} (${registration.participantEmail}) — $${registration.feeAmount}`,
    metadata: { amount: registration.feeAmount },
  });
  return result?.data;
}
